const DATA_URL_PATTERN = /^data:image\/(?:png|jpe?g|webp);base64,/i;

export const LOGO_MAX_WIDTH = 1600;
export const LOGO_MAX_HEIGHT = 1600;
export const LOGO_MAX_BYTES = 100 * 1024;
const LOGO_MIN_LONG_EDGE = 128;

interface LoadedLogo {
  source: CanvasImageSource;
  width: number;
  height: number;
}

export interface LogoOptimizationRuntime {
  load(dataUrl: string): Promise<LoadedLogo>;
  render(source: CanvasImageSource, width: number, height: number): string;
}

function loadBrowserImage(dataUrl: string): Promise<LoadedLogo> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.decoding = 'async';
    image.onload = () => {
      const width = image.naturalWidth || image.width;
      const height = image.naturalHeight || image.height;
      if (width <= 0 || height <= 0) {
        reject(new Error('Logo has invalid dimensions'));
        return;
      }
      resolve({ source: image, width, height });
    };
    image.onerror = () => reject(new Error('Logo could not be decoded'));
    image.src = dataUrl;
  });
}

function renderBrowserPng(source: CanvasImageSource, width: number, height: number): string {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Logo canvas is unavailable');

  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  context.clearRect(0, 0, width, height);
  context.drawImage(source, 0, 0, width, height);
  return canvas.toDataURL('image/png');
}

const browserRuntime: LogoOptimizationRuntime = {
  load: loadBrowserImage,
  render: renderBrowserPng,
};

export function dataUrlByteLength(dataUrl: string): number {
  const comma = dataUrl.indexOf(',');
  if (comma < 0 || !/;base64$/i.test(dataUrl.slice(0, comma))) {
    throw new Error('Logo must be a base64 image data URL');
  }
  const payload = dataUrl.slice(comma + 1).replace(/\s/g, '');
  const padding = payload.endsWith('==') ? 2 : payload.endsWith('=') ? 1 : 0;
  return Math.max(0, Math.floor((payload.length * 3) / 4) - padding);
}

export function fitLogoDimensions(
  width: number,
  height: number,
  maxWidth = LOGO_MAX_WIDTH,
  maxHeight = LOGO_MAX_HEIGHT,
): { width: number; height: number } {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    throw new Error('Logo has invalid dimensions');
  }
  const scale = Math.min(1, maxWidth / width, maxHeight / height);
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/**
 * Normalizes dashboard logo uploads before they reach the shared image bucket.
 * The entire canvas is scaled without cropping so transparent padding and the
 * merchant's intended composition remain unchanged.
 */
export async function optimizeLogoDataUrl(
  dataUrl: string,
  runtime: LogoOptimizationRuntime = browserRuntime,
): Promise<string> {
  if (!DATA_URL_PATTERN.test(dataUrl)) {
    throw new Error('Logo must be a PNG, JPEG, or WebP image');
  }

  const loaded = await runtime.load(dataUrl);
  let dimensions = fitLogoDimensions(loaded.width, loaded.height);
  let optimized = runtime.render(loaded.source, dimensions.width, dimensions.height);

  while (
    dataUrlByteLength(optimized) > LOGO_MAX_BYTES &&
    Math.max(dimensions.width, dimensions.height) > LOGO_MIN_LONG_EDGE
  ) {
    const byteRatio = LOGO_MAX_BYTES / dataUrlByteLength(optimized);
    const scale = Math.min(0.85, Math.max(0.5, Math.sqrt(byteRatio) * 0.9));
    dimensions = {
      width: Math.max(1, Math.round(dimensions.width * scale)),
      height: Math.max(1, Math.round(dimensions.height * scale)),
    };
    optimized = runtime.render(loaded.source, dimensions.width, dimensions.height);
  }

  if (dataUrlByteLength(optimized) > LOGO_MAX_BYTES) {
    throw new Error('Logo could not be reduced below 100 KB');
  }
  return optimized;
}
